import React, { useRef } from 'react';
import { Badge, Button, Card, Col, ProgressBar, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';

import { useTypedProjectCostContext } from './useTypedProjectCostContext';
import { formatCurrencyFromCents, formatPercent } from './utils';

const ProjectsCostHubPage: React.FC = () => {
  const {
    setupDiagnostics,
    companyStatsList,
    calcResult,
    analysisHistory,
    companies,
    roles,
    activeCollaboratorsCount,
    demandRates,
    companyRoleRates,
    setShowCollaboratorImportModal,
    setShowCollaboratorJiraImportModal,
    handleResolveJiraIds,
    handleExportCostConfig,
    handleImportCostConfig,
    configSyncing,
    saving,
    resolveJiraStatus,
    openNewCompany,
    openNewRole,
    openNewCollaborator,
    openNewDemandRate,
    openNewCompanyRoleRate,
    openNewCollaboratorRate,
  } = useTypedProjectCostContext<{
    setupDiagnostics: {
      checklist: Array<{ id: string; label: string; ok: boolean; help: string }>;
      readinessPercent: number;
    };
    companyStatsList: Array<{
      companyId: number;
      companyName: string;
      collaboratorsCount: number;
      companyRoleRatesCount: number;
      collaboratorRatesCount: number;
      mappingPercent: number;
      activeCollaboratorsCount: number;
    }>;
    calcResult: { total_cost_cents: number; total_revenue_cents: number } | null;
    analysisHistory: unknown[];
    companies: unknown[];
    roles: unknown[];
    activeCollaboratorsCount: number;
    demandRates: unknown[];
    companyRoleRates: unknown[];
    setShowCollaboratorImportModal: (show: boolean) => void;
    setShowCollaboratorJiraImportModal: (show: boolean) => void;
    handleResolveJiraIds: () => Promise<void>;
    handleExportCostConfig: () => Promise<void>;
    handleImportCostConfig: (file: File) => Promise<void>;
    configSyncing: boolean;
    saving: boolean;
    resolveJiraStatus: {
      running: boolean;
      stage: 'idle' | 'preview' | 'confirm' | 'apply' | 'refresh' | 'done' | 'error';
      progressPct: number;
      message: string;
    };
    openNewCompany: () => void;
    openNewRole: () => void;
    openNewCollaborator: (companyId?: string) => void;
    openNewDemandRate: () => void;
    openNewCompanyRoleRate: (companyId?: string) => void;
    openNewCollaboratorRate: () => void;
  }>();
  const costsImportInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <>
      <Row className="g-3 mb-3">
        <Col md={2}>
          <Card className="cost-kpi">
            <Card.Body>
              <div>Empresas</div>
              <strong>{companies.length}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="cost-kpi">
            <Card.Body>
              <div>Funções</div>
              <strong>{roles.length}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="cost-kpi">
            <Card.Body>
              <div>Colaboradores</div>
              <strong>{activeCollaboratorsCount}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="cost-kpi">
            <Card.Body>
              <div>Taxas OS/AS</div>
              <strong>{demandRates.length}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="cost-kpi">
            <Card.Body>
              <div>Regras empresa+função</div>
              <strong>{companyRoleRates.length}</strong>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Empresas cadastradas</span>
          <Badge bg="primary">{companyStatsList.length}</Badge>
        </Card.Header>
        <Card.Body>
          {companyStatsList.length > 0 ? (
            <div className="cost-company-cards-grid">
              {companyStatsList.map((stats) => (
                <Link
                  key={stats.companyId}
                  to={`/projects/costs/empresas/${stats.companyId}/resumo`}
                  className="cost-company-card text-decoration-none"
                >
                  <div className="fw-semibold text-dark">{stats.companyName}</div>
                  <div className="small text-muted mt-1">
                    {stats.collaboratorsCount} colaborador(es) · {stats.companyRoleRatesCount} regra(s)
                  </div>
                  <div className="small mt-1">
                    Mapeamento Jira:{' '}
                    <Badge bg={stats.mappingPercent >= 98 ? 'success' : 'warning'}>
                      {formatPercent(stats.mappingPercent)}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="small text-muted">
              Nenhuma empresa cadastrada. Use <strong>Configurações → Empresas</strong> ou o botão{' '}
              <strong>Nova empresa</strong> em Ações globais.
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Prontidão da configuração (antes de calcular)</Card.Header>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <strong>{formatPercent(setupDiagnostics.readinessPercent)} pronto</strong>
            <small className="text-muted">
              Objetivo: evitar custo zerado e análise inconsistente por falta de cadastro.
            </small>
          </div>
          <div className="cost-readiness-bar mb-2">
            <div
              className="cost-readiness-bar__fill"
              style={{ width: `${Math.max(0, Math.min(100, setupDiagnostics.readinessPercent))}%` }}
            />
          </div>
          <div className="cost-readiness-grid">
            {setupDiagnostics.checklist.map((item) => (
              <div key={item.id} className={`cost-readiness-item ${item.ok ? 'is-ok' : 'is-missing'}`}>
                <div className="fw-semibold">{item.ok ? 'OK' : 'Pendente'} — {item.label}</div>
                <small className="text-muted">{item.help}</small>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-2">Como usar este módulo de custos</h6>
          <ol className="mb-2 ps-3">
            <li>Use <strong>Configurações</strong> para manter empresas, funções e taxas globais.</li>
            <li>Entre em cada <strong>empresa</strong> para gerenciar colaboradores e regras específicas.</li>
            <li>Use <strong>Análise</strong> para rodar o período desejado e analisar custo/receita/margem.</li>
            <li>Use <strong>Histórico</strong> para comparar análises e reaplicar filtros rapidamente.</li>
          </ol>
          <div className="d-flex flex-wrap gap-2">
            <LinkContainer to="/projects/costs/configuracoes">
              <Button size="sm" variant="outline-primary">Ir para Configurações</Button>
            </LinkContainer>
            <LinkContainer to="/projects/costs/analise">
              <Button size="sm" variant="outline-primary">Ir para Análise</Button>
            </LinkContainer>
            <LinkContainer to="/projects/costs/historico">
              <Button size="sm" variant="outline-primary">Ir para Histórico</Button>
            </LinkContainer>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body className="py-2">
          <strong>Status atual</strong>
          <div className="small text-muted mt-1">
            Último resultado carregado:{' '}
            {calcResult
              ? `${formatCurrencyFromCents(calcResult.total_cost_cents)} de custo | ${formatCurrencyFromCents(calcResult.total_revenue_cents)} de receita`
              : 'nenhum cálculo nesta sessão'}
          </div>
          <div className="small text-muted">
            Histórico salvo localmente: {analysisHistory.length} execução(ões).
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Ações globais</Card.Header>
        <Card.Body className="d-flex flex-wrap gap-2">
          <input
            ref={costsImportInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              void handleImportCostConfig(file);
              event.currentTarget.value = '';
            }}
          />
          <Button size="sm" variant="outline-success" onClick={() => void handleExportCostConfig()} disabled={configSyncing}>
            Exportar custos (JSON)
          </Button>
          <Button size="sm" variant="outline-success" onClick={() => costsImportInputRef.current?.click()} disabled={configSyncing}>
            Importar custos (JSON)
          </Button>
          <Button size="sm" onClick={openNewCompany}>Nova empresa</Button>
          <Button size="sm" onClick={openNewRole}>Nova função</Button>
          <Button size="sm" onClick={() => openNewCollaborator()}>Novo colaborador</Button>
          <Button size="sm" variant="outline-primary" onClick={() => setShowCollaboratorImportModal(true)}>
            Importar colaboradores
          </Button>
          <Button size="sm" variant="outline-primary" onClick={() => setShowCollaboratorJiraImportModal(true)}>
            Importar colaboradores do Jira
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => void handleResolveJiraIds()}
            disabled={saving || resolveJiraStatus.running}
          >
            {resolveJiraStatus.running ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Resolvendo Jira IDs...
              </>
            ) : (
              'Resolver Jira IDs'
            )}
          </Button>
          <Button size="sm" onClick={openNewDemandRate}>Nova taxa OS/AS</Button>
          <Button size="sm" onClick={() => openNewCompanyRoleRate()}>Nova regra empresa+função</Button>
          <Button size="sm" variant="outline-dark" onClick={openNewCollaboratorRate}>
            Nova taxa temporal do colaborador
          </Button>
          {!!resolveJiraStatus.message && (
            <div className="w-100 mt-2">
              <div className={`small ${resolveJiraStatus.stage === 'error' ? 'text-danger' : 'text-muted'}`}>
                {resolveJiraStatus.message}
              </div>
              {(resolveJiraStatus.running || resolveJiraStatus.progressPct > 0) && (
                <ProgressBar
                  now={Math.max(0, Math.min(100, resolveJiraStatus.progressPct))}
                  variant={resolveJiraStatus.stage === 'error' ? 'danger' : 'secondary'}
                  className="mt-1"
                  label={`${Math.round(resolveJiraStatus.progressPct)}%`}
                />
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default ProjectsCostHubPage;
